import { type FormEvent, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import type { Person, Role, Site, Visit } from "../../shared/types";
import { ErrorBanner, Field, Loading } from "../components/ui";
import { patch, put } from "../lib/api";
import { addDaysIso, fmtDuration, isoLocalDate } from "../lib/format";
import { useApi } from "../lib/useApi";
import { useSession } from "../session";
import {
  Check,
  ConfirmDialog,
  changedFields,
  LoadState,
  orNull,
  PageHeader,
  toApiError,
  useRecordForm,
  useSites,
} from "./common";
import { visitTotals } from "./payable";
import { NotPayable, PAYABLE_HINT, VisitTable } from "./TimesheetsPage";

interface PersonForm {
  name: string;
  role: Role;
  phone: string;
  qbName: string;
  qbEntity: "employee" | "supplier";
  qbId: string;
}

const toForm = (p: Person): PersonForm => ({
  name: p.name,
  role: p.role,
  phone: p.phone ?? "",
  qbName: p.qbName ?? "",
  qbEntity: p.qbEntity,
  qbId: p.qbId ?? "",
});

const toPayload = (f: PersonForm) => ({
  name: f.name.trim(),
  role: f.role,
  phone: orNull(f.phone),
  qbName: orNull(f.qbName),
  qbEntity: f.qbEntity,
  qbId: orNull(f.qbId),
});

function DetailsForm({ person, onSaved }: { person: Person; onSaved: (p: Person) => void }) {
  const [form, setForm] = useRecordForm(person, person.id, toForm);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: "ok" | "danger"; text: string } | null>(null);
  const set = <K extends keyof PersonForm>(k: K, v: PersonForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    const changes = changedFields(toPayload(toForm(person)), toPayload(form));
    if (!Object.keys(changes).length) return setStatus({ kind: "danger", text: "Nothing has changed." });
    setBusy(true);
    setStatus(null);
    try {
      const np = await patch<Person>(`/admin/people/${person.id}`, changes);
      setForm(toForm(np));
      onSaved(np);
      setStatus({ kind: "ok", text: "Saved." });
    } catch (err) {
      setStatus({ kind: "danger", text: toApiError(err).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card stack" onSubmit={submit}>
      <h2>Details</h2>
      <div className="grid cols-2">
        <Field label="Name">
          <input value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120} required />
        </Field>
        <Field label="Email" hint="Can't be changed here. Add a new person if their email changes.">
          <input value={person.email} readOnly disabled />
        </Field>
        <Field label="Role">
          <select value={form.role} onChange={(e) => set("role", e.target.value as Role)}>
            <option value="cleaner">Cleaner</option>
            <option value="admin">Admin (can see and change everything)</option>
          </select>
        </Field>
        <Field label="Phone">
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            maxLength={30}
          />
        </Field>
        <Field label="QuickBooks name" hint="Exactly as in QuickBooks. Leave empty to use their name.">
          <input value={form.qbName} onChange={(e) => set("qbName", e.target.value)} maxLength={200} />
        </Field>
        <Field label="In QuickBooks they are">
          <select
            value={form.qbEntity}
            onChange={(e) => set("qbEntity", e.target.value as "employee" | "supplier")}
          >
            <option value="employee">An employee</option>
            <option value="supplier">A supplier (self-employed)</option>
          </select>
        </Field>
        <Field label="QuickBooks ID" hint="Only needed for sending time straight to QuickBooks.">
          <input value={form.qbId} onChange={(e) => set("qbId", e.target.value)} maxLength={64} />
        </Field>
      </div>
      {status && <div className={`banner ${status.kind}`}>{status.text}</div>}
      <div className="row">
        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? "Saving…" : "Save details"}
        </button>
      </div>
    </form>
  );
}

/** Assigned site ids in a stable order, so "unchanged" compares equal. */
const siteIdsOf = (p: Person) => [...p.siteIds].sort();

function SitesForm({
  person,
  sites,
  onSaved,
}: {
  person: Person;
  sites: Site[];
  onSaved: (p: Person) => void;
}) {
  const [chosenIds, setChosenIds] = useRecordForm(person, person.id, siteIdsOf);
  const chosen = new Set(chosenIds);
  const setChosen = (fn: (prev: Set<string>) => Set<string>) =>
    setChosenIds((ids) => [...fn(new Set(ids))].sort());
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: "ok" | "danger"; text: string } | null>(null);
  const dirty = chosen.size !== person.siteIds.length || person.siteIds.some((id) => !chosen.has(id));
  // Show active sites, plus any inactive ones they're still assigned to.
  const shown = sites.filter((s) => s.active || person.siteIds.includes(s.id));

  async function save() {
    setBusy(true);
    setStatus(null);
    try {
      const np = await put<Person>(`/admin/people/${person.id}/sites`, { siteIds: [...chosen] });
      setChosenIds(siteIdsOf(np));
      onSaved(np);
      setStatus({ kind: "ok", text: "Sites saved." });
    } catch (err) {
      setStatus({ kind: "danger", text: toApiError(err).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack">
      <h2>Assigned sites</h2>
      <p className="muted small">
        These are the sites they can pick from if a sticker can't be scanned, and clocking in anywhere else is
        flagged “Not assigned”. With no sites ticked they can't pick any site, and every clock-in is flagged.
      </p>
      {shown.length ? (
        <div className="grid cols-2" style={{ gap: 4 }}>
          {shown.map((s) => (
            <Check
              key={s.id}
              label={
                <>
                  {s.name}
                  {!s.active && <span className="muted"> (inactive)</span>}
                </>
              }
              checked={chosen.has(s.id)}
              onChange={(on) =>
                setChosen((prev) => {
                  const next = new Set(prev);
                  if (on) next.add(s.id);
                  else next.delete(s.id);
                  return next;
                })
              }
            />
          ))}
        </div>
      ) : (
        <p className="muted">
          No sites yet. <Link to="/admin/sites/new">Add a site</Link>.
        </p>
      )}
      {status && <div className={`banner ${status.kind}`}>{status.text}</div>}
      <div className="row">
        <button className="btn primary" onClick={save} disabled={busy || !dirty}>
          {busy ? "Saving…" : "Save sites"}
        </button>
      </div>
    </section>
  );
}

export function PersonPage() {
  const { id = "" } = useParams();
  const location = useLocation();
  const { me } = useSession();
  const person = useApi<Person>(`/admin/people/${encodeURIComponent(id)}`);
  const sites = useSites();
  const today = isoLocalDate();
  const from = addDaysIso(today, -29);
  const visits = useApi<Visit[]>(`/admin/visits?from=${from}&to=${today}&userId=${encodeURIComponent(id)}`);
  const [confirming, setConfirming] = useState(false);
  const arrived = location.state as { message?: string; tone?: "ok" | "warn" } | null;
  const [message, setMessage] = useState<{ text: string; tone: "ok" | "warn" } | null>(
    arrived?.message ? { text: arrived.message, tone: arrived.tone === "warn" ? "warn" : "ok" } : null,
  );
  const totals = useMemo(() => visitTotals(visits.data ?? []), [visits.data]);

  const back = { to: "/admin/people", label: "Cleaners" };
  if (!person.data) {
    return (
      <div className="page">
        <PageHeader title="Person" back={back} />
        {person.error ? <ErrorBanner error={person.error} onRetry={person.reload} /> : <Loading />}
      </div>
    );
  }
  const p = person.data;
  const isMe = me?.user.id === p.id;

  return (
    <div className="page">
      <PageHeader
        title={p.name}
        sub={
          <span className="row" style={{ gap: 6 }}>
            {p.email}
            {p.role === "admin" && <span className="badge info">Admin</span>}
            {p.active ? <span className="badge ok">Active</span> : <span className="badge">Deactivated</span>}
          </span>
        }
        back={back}
        actions={
          !isMe && (
            <button className={`btn${p.active ? "" : " primary"}`} onClick={() => setConfirming(true)}>
              {p.active ? "Deactivate" : "Reactivate"}
            </button>
          )
        }
      />
      {message && <div className={`banner ${message.tone}`}>{message.text}</div>}
      {!p.active && (
        <div className="banner warn">This person can't sign in or clock in. Their past visits are kept.</div>
      )}

      <div className="stack">
        <DetailsForm
          person={p}
          onSaved={(np) => {
            person.setData(np);
            setMessage(null);
          }}
        />
        <LoadState state={sites}>
          {(list) => <SitesForm person={p} sites={list} onSaved={(np) => person.setData(np)} />}
        </LoadState>

        <section className="adm-section">
          <div className="row between">
            <h2 style={{ margin: 0 }}>Last 30 days</h2>
            <Link to={`/admin/timesheets?userId=${p.id}&from=${from}&to=${today}`} className="btn ghost sm">
              Open in Timesheets
            </Link>
          </div>
          <LoadState state={visits}>
            {(list) => (
              <div className="stack">
                <p className="muted" title={PAYABLE_HINT}>
                  {list.length} {list.length === 1 ? "visit" : "visits"} ·{" "}
                  {fmtDuration(totals.payableMinutes)} payable
                  {totals.notPayableVisits > 0 && (
                    <>
                      {" "}
                      · not payable yet: <NotPayable t={totals} />
                    </>
                  )}
                </p>
                <VisitTable visits={list} showPerson={false} />
              </div>
            )}
          </LoadState>
        </section>
      </div>

      {confirming && (
        <ConfirmDialog
          title={p.active ? `Deactivate ${p.name}?` : `Reactivate ${p.name}?`}
          confirmLabel={p.active ? "Deactivate" : "Reactivate"}
          danger={p.active}
          onConfirm={async () => {
            const np = await patch<Person>(`/admin/people/${p.id}`, { active: !p.active });
            person.setData(np);
            setMessage({
              text: np.active ? `${np.name} can sign in again.` : `${np.name} has been deactivated.`,
              tone: "ok",
            });
          }}
          onClose={() => setConfirming(false)}
        >
          <p>
            {p.active
              ? "They'll be signed out and won't be able to sign in or clock in. Their past visits and hours are kept for exports."
              : "They'll be able to sign in and clock in again."}
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
